import { useAtomValue } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationDataAtom } from '@/store';
import { businessQueryOptions } from '@/lib/query-options';
import { addLandingPage, getLandingPagesDetails } from '@/lib/apis';
import { LandingPageCombobox, type LandingPageEntry } from './landing-page-combobox';

// Multi-part TLDs for same-domain filtering
const MULTI_PART_TLDS = new Set([
  'co.uk', 'com.au', 'co.nz', 'co.jp', 'com.br', 'co.in',
  'org.uk', 'net.au', 'co.za', 'com.mx', 'co.kr', 'com.sg',
  'com.hk', 'co.il', 'com.ar', 'com.tw', 'co.th', 'com.my',
]);

const getBaseDomain = (hostname: string): string => {
  const withoutWww = hostname.toLowerCase().replace(/^www\./, '');
  const parts = withoutWww.split('.');
  if (parts.length >= 3 && MULTI_PART_TLDS.has(parts.slice(-2).join('.'))) {
    return parts.slice(-3).join('.');
  }
  return parts.length >= 2 ? parts.slice(-2).join('.') : withoutWww;
};

const getLabelFromUrl = (url: string): string => {
  try {
    const u = new URL(url);
    const segs = u.pathname.split('/').filter(Boolean);
    const last = segs[segs.length - 1];
    return last ? last.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : u.hostname;
  } catch {
    return url;
  }
};

const ensureProtocol = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const normalizeUrl = (url: string): string =>
  url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

interface BusinessLandingPageComboboxProps {
  selectedUrl?: string | null;
  onSelect: (entry: LandingPageEntry | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * When set, only entries from this domain are shown in the dropdown, and
   * newly typed URLs are domain-validated before being saved to the business.
   * Used by the "Add Ad" modal to restrict to the campaign's domain.
   */
  filterByDomain?: string;
  /** Forwarded to the search input so a `<Label htmlFor>` can associate with it (#6182). */
  inputId?: string;
}

export function BusinessLandingPageCombobox({
  selectedUrl,
  onSelect,
  placeholder = 'Select a landing page...',
  disabled = false,
  filterByDomain,
  inputId,
}: BusinessLandingPageComboboxProps) {
  const organizationData = useAtomValue(organizationDataAtom);
  const queryClient = useQueryClient();

  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id ?? 0),
    enabled: !!organizationData?.id,
  });

  const { data: landingPagesDetails = [] } = useQuery({
    queryKey: ['landingPagesDetails', business?.id],
    queryFn: () => getLandingPagesDetails(business!.id),
    enabled: !!business?.id,
  });

  // Show every landing page from the assets source of truth. The homepage is included
  // as its own entry only when the source list doesn't already carry it, keyed by
  // normalized URL so a scheme/www variant of the homepage is never listed twice.
  const allEntries: LandingPageEntry[] = [];
  const seenKeys = new Set<string>();

  for (const lp of landingPagesDetails) {
    const key = normalizeUrl(lp.url);
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    allEntries.push({
      id: `lp_${lp.url}`,
      label: lp.title ?? getLabelFromUrl(lp.url),
      url: lp.url,
      type: business && key === normalizeUrl(business.business_url) ? 'homepage' : 'custom',
    });
  }

  if (business?.business_url && !seenKeys.has(normalizeUrl(business.business_url))) {
    allEntries.unshift({
      id: '_homepage',
      label: business.brand_name ?? 'Homepage',
      url: ensureProtocol(business.business_url),
      type: 'homepage',
    });
  }

  // When filterByDomain is active, only show same-domain entries
  const entries = filterByDomain
    ? allEntries.filter((e) => {
        try {
          return (
            getBaseDomain(new URL(ensureProtocol(e.url)).hostname) ===
            getBaseDomain(new URL(ensureProtocol(filterByDomain)).hostname)
          );
        } catch {
          return false;
        }
      })
    : allEntries;

  // Derive the display entry from the URL string
  const value: LandingPageEntry | null = selectedUrl
    ? (entries.find((e) => normalizeUrl(e.url) === normalizeUrl(selectedUrl)) ??
        allEntries.find((e) => normalizeUrl(e.url) === normalizeUrl(selectedUrl)) ?? {
          id: `custom_${selectedUrl}`,
          label: getLabelFromUrl(selectedUrl),
          url: selectedUrl,
          type: 'custom',
        })
    : null;

  /**
   * When filterByDomain is active we intercept onSelect to manually handle
   * saving new typed URLs (after domain validation), because the underlying
   * combobox is given no businessId and won't auto-save.
   */
  const handleSelect = async (entry: LandingPageEntry | null) => {
    if (!entry || !entry.id.startsWith('custom_') || !filterByDomain || !business?.id) {
      onSelect(entry);
      return;
    }

    // New typed URL with domain filtering — save only if domain matches
    try {
      const entryDomain = getBaseDomain(new URL(ensureProtocol(entry.url)).hostname);
      const filterDomain = getBaseDomain(new URL(ensureProtocol(filterByDomain)).hostname);
      if (entryDomain === filterDomain) {
        await addLandingPage(business.id, entry.url);
        queryClient.invalidateQueries({ queryKey: ['landingPagesDetails', business.id] });
      }
    } catch {
      // URL parse failed — pass through, consumer can show an error
    }

    onSelect(entry);
  };

  return (
    <LandingPageCombobox
      entries={entries}
      // When domain-filtering, don't pass businessId to the combobox — we handle
      // saving manually above to prevent cross-domain URLs reaching the business.
      businessId={filterByDomain ? undefined : business?.id}
      value={value}
      onSelect={handleSelect}
      placeholder={placeholder}
      disabled={disabled}
      inputId={inputId}
    />
  );
}
